import { useEffect, useState } from "react";
import api from "@/lib/api";
import { STATUSES, priorityMeta, phaseColor } from "@/lib/constants";
import { GripVertical, BookOpen, Plus, Loader2 } from "lucide-react";
import { toast } from "sonner";

const STORY_STATUS = [
  { key: "backlog", label: "Backlog", color: "#9CA3AF" },
  { key: "ready", label: "Ready", color: "#F59E0B" },
  { key: "in_progress", label: "In progress", color: "#10B981" },
  { key: "done", label: "Done", color: "#2563EB" },
];

export default function KanbanBoard({ projectId, tasks, onStatusChange, onOpenTask, phaseColors }) {
  const [dragId, setDragId] = useState(null);
  const [overCol, setOverCol] = useState(null);
  const [stories, setStories] = useState([]);
  const [newStory, setNewStory] = useState("");
  const [adding, setAdding] = useState(false);
  const colorOf = (p) => (phaseColors && phaseColors[p]) || phaseColor(p);

  const loadStories = () => {
    if (!projectId) return;
    api.get(`/projects/${projectId}/stories`).then(({ data }) => setStories(data)).catch(() => {});
  };
  useEffect(() => { loadStories(); }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  const backlogStories = stories.filter((s) => s.status !== "done");

  const addStory = async () => {
    const title = newStory.trim();
    if (!title) return;
    setAdding(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/stories`, { title, description: "", priority: "medium", points: null, status: "backlog", linked_task_id: null });
      setStories((arr) => [...arr, data]);
      setNewStory("");
      toast.success(`Story ${data.display_id} added to the backlog`);
    } catch (e) { toast.error("Could not add the story"); }
    finally { setAdding(false); }
  };

  const setStoryStatus = async (s, status) => {
    try {
      const { title, description, priority, points, linked_task_id } = s;
      await api.put(`/stories/${s.id}`, { title, description: description || "", priority: priority || "medium", points: points ?? null, status, linked_task_id: linked_task_id || null });
      setStories((arr) => arr.map((x) => (x.id === s.id ? { ...x, status } : x)));
    } catch (e) { toast.error("Could not update the story"); }
  };

  const drop = (statusKey) => {
    if (dragId) {
      const t = tasks.find((x) => x.id === dragId);
      if (t && t.status !== statusKey) onStatusChange(dragId, statusKey);
    }
    setDragId(null);
    setOverCol(null);
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4" data-testid="kanban-board">
      {/* User story backlog column */}
      <div className="rounded-lg border border-dashed border-violet-300 bg-violet-50/40 p-3 min-h-[200px]" data-testid="kanban-column-backlog">
        <div className="flex items-center justify-between mb-3 px-1">
          <div className="flex items-center gap-2">
            <BookOpen className="h-3.5 w-3.5 text-violet-600" />
            <span className="font-semibold text-sm text-violet-800">Backlog</span>
          </div>
          <span className="text-xs text-muted-foreground bg-background border border-border rounded-full px-2 py-0.5">{backlogStories.length}</span>
        </div>
        <div className="flex items-center gap-1.5 mb-3">
          <input
            value={newStory}
            onChange={(e) => setNewStory(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") addStory(); }}
            placeholder="As a user, I want…"
            className="h-8 flex-1 min-w-0 rounded border border-border bg-background px-2 text-xs"
            data-testid="kanban-story-input"
          />
          <button onClick={addStory} disabled={adding || !newStory.trim()}
            className="h-8 w-8 shrink-0 rounded bg-violet-600 text-white flex items-center justify-center hover:bg-violet-700 disabled:opacity-40 transition-colors"
            title="Add a user story to the backlog" data-testid="kanban-story-add">
            {adding ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
          </button>
        </div>
        <div className="space-y-2">
          {backlogStories.map((s) => {
            const pm = priorityMeta(s.priority);
            const st = STORY_STATUS.find((x) => x.key === s.status) || STORY_STATUS[0];
            return (
              <div key={s.id} className="bg-card border border-border rounded-md p-3" data-testid={`kanban-story-${s.id}`}>
                <div className="flex items-start gap-1.5">
                  <span className="font-mono text-[10px] font-bold text-violet-700 shrink-0 mt-0.5" data-testid={`kanban-story-id-${s.id}`}>{s.display_id}</span>
                  <p className="text-sm font-medium leading-snug min-w-0 flex-1">{s.title}</p>
                </div>
                <div className="flex items-center flex-wrap gap-1.5 mt-2">
                  <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded" style={{ color: pm.color, background: `${pm.color}1a` }}>{pm.label}</span>
                  {s.points != null && <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-secondary text-muted-foreground">{s.points} pts</span>}
                  <select
                    value={s.status}
                    onChange={(e) => setStoryStatus(s, e.target.value)}
                    className="ml-auto h-6 rounded border border-border bg-background px-1 text-[10px] font-semibold"
                    style={{ color: st.color }}
                    title="Update this story's status"
                    data-testid={`kanban-story-status-${s.id}`}
                  >
                    {STORY_STATUS.map((x) => <option key={x.key} value={x.key}>{x.label}</option>)}
                  </select>
                </div>
              </div>
            );
          })}
          {backlogStories.length === 0 && (
            <p className="text-[11px] text-muted-foreground px-1" data-testid="kanban-backlog-empty">No user stories yet — add one above. IDs are generated automatically.</p>
          )}
        </div>
      </div>

      {STATUSES.map((col) => {
        const items = tasks.filter((t) => t.status === col.key);
        return (
          <div
            key={col.key}
            onDragOver={(e) => { e.preventDefault(); setOverCol(col.key); }}
            onDragLeave={() => setOverCol((c) => (c === col.key ? null : c))}
            onDrop={() => drop(col.key)}
            className={`rounded-lg border p-3 min-h-[200px] transition-colors ${overCol === col.key ? "border-primary bg-primary/5" : "border-border bg-secondary/30"}`}
            data-testid={`kanban-column-${col.key}`}
          >
            <div className="flex items-center justify-between mb-3 px-1">
              <div className="flex items-center gap-2">
                <span className="h-2.5 w-2.5 rounded-full" style={{ background: col.color }} />
                <span className="font-semibold text-sm">{col.label}</span>
              </div>
              <span className="text-xs text-muted-foreground bg-background border border-border rounded-full px-2 py-0.5">{items.length}</span>
            </div>
            <div className="space-y-2">
              {items.map((t) => {
                const pm = priorityMeta(t.priority);
                return (
                  <div
                    key={t.id}
                    draggable
                    onDragStart={() => setDragId(t.id)}
                    onDragEnd={() => setDragId(null)}
                    onClick={() => onOpenTask(t)}
                    className="group bg-card border border-border rounded-md p-3 cursor-pointer hover:border-foreground/30 hover:shadow-sm transition-all"
                    data-testid={`kanban-card-${t.id}`}
                  >
                    <div className="flex items-start gap-2">
                      <GripVertical className="h-4 w-4 text-muted-foreground/40 mt-0.5 shrink-0" />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium leading-snug">{t.title}</p>
                        <div className="flex items-center flex-wrap gap-1.5 mt-2">
                          <span className="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded" style={{ color: colorOf(t.phase), background: `${colorOf(t.phase)}1a` }}>
                            {t.phase}
                          </span>
                          <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded" style={{ color: pm.color, background: `${pm.color}1a` }}>
                            {pm.label}
                          </span>
                        </div>
                        {(t.assignee || t.end_date) && (
                          <div className="flex items-center justify-between mt-2 text-[11px] text-muted-foreground">
                            <span className="truncate">{t.assignee || "Unassigned"}</span>
                            {t.end_date && <span>{t.end_date}</span>}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
